import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Google Analytics 4 (gtag.js) integration for the marketing SPA.
 *
 * Design notes:
 * - Fully inert when environment.gaMeasurementId is empty: gtag.js is never
 *   injected and no events fire. That keeps local/dev/stg clean and means the
 *   whole feature ships "off" until a real G-XXXXXXXXXX id is set in prod.
 * - Automatic page_view is disabled ({ send_page_view: false }) because Angular
 *   routing does not trigger real page loads. We send one page_view per
 *   NavigationEnd instead, so route changes are counted as distinct views.
 * - No cookie/consent banner is wired yet. When one is added, gate init() on
 *   consent (or switch to GA Consent Mode) — the single entry point makes that
 *   a one-place change.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly measurementId = environment.gaMeasurementId;
  private initialized = false;

  /** True only when a measurement id is configured for this environment. */
  get enabled(): boolean {
    return !!this.measurementId;
  }

  /**
   * Inject gtag.js and start tracking route changes. Safe to call once at app
   * start; a no-op when analytics is disabled or already initialized, or when
   * running outside a browser (e.g. the prerender pass, which has no window).
   */
  init(): void {
    if (this.initialized || !this.enabled) return;
    const win = this.document.defaultView as (Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void }) | null;
    if (!win) return; // no browser context (SSR/prerender) — skip.
    this.initialized = true;

    // Standard gtag bootstrap. dataLayer + gtag shim must exist before the
    // remote script loads so queued calls are replayed once it is ready.
    // The shim MUST push the `arguments` object, not a rest-param array.
    // gtag.js silently ignores plain arrays, so no hits would ever be sent.
    win.dataLayer = win.dataLayer || [];
    const gtag = function (..._args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer!.push(arguments);
    };
    win.gtag = gtag;

    gtag('js', new Date());
    // send_page_view:false — we send page_view manually per navigation below.
    // Google signals and ad personalization stay off so GA never sets
    // advertising cookies. Privacy Policy 9.2 depends on this (VOFF-399).
    // [[OWNER: turn off Google signals in the GA4 property too, and decide whether GA4 runs inside the signed-in app]]
    gtag('config', this.measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    this.document.head.appendChild(script);

    this.trackNavigation();
  }

  /** Fire a GA4 page_view for the current URL on every completed navigation. */
  private trackNavigation(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        this.pageView(e.urlAfterRedirects);
      });
  }

  /** Send a page_view event. */
  pageView(path: string): void {
    const href = this.document.defaultView?.location.href;
    this.event('page_view', {
      page_path: withoutSecrets(path),
      page_location: href ? withoutSecrets(href) : href,
      page_title: this.document.title,
    });
  }

  /**
   * Send an arbitrary GA4 event. Use for conversions such as
   * `employer_call_booked`, `university_call_booked`, `sign_up`,
   * `begin_checkout`. No-op when analytics is disabled.
   */
  event(name: string, params: Record<string, unknown> = {}): void {
    if (!this.enabled) return;
    const gtag = (this.document.defaultView as (Window & { gtag?: (...args: unknown[]) => void }) | null)?.gtag;
    gtag?.('event', name, params);
  }
}

/** Query params that carry one-time secrets and must never reach analytics. */
// One-time codes and personal data that must never reach analytics:
// password reset (oobCode), seat invitations (seat, email), join codes (code),
// LinkedIn sign-in hand-off (tx), and any generic token.
const SECRET_PARAMS = ['oobCode', 'seat', 'email', 'code', 'tx', 'token'];

/** Drop secret query params from a path or absolute URL. */
export function withoutSecrets(url: string): string {
  if (!SECRET_PARAMS.some(p => url.includes(`${p}=`))) return url;
  const parsed = new URL(url, 'https://placeholder.invalid');
  for (const p of SECRET_PARAMS) parsed.searchParams.delete(p);
  const rest = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return parsed.origin === 'https://placeholder.invalid' ? rest : `${parsed.origin}${rest}`;
}
